/**
 * Driver Service
 * Handles driver management operations for providers
 */

import { apiClient, ApiClient } from './api';

/**
 * Driver types
 */
export type DriverStatus = 'active' | 'inactive' | 'on_leave';
export type CDLClass = 'A' | 'B' | 'C';

/**
 * Driver DTO
 */
export interface DriverDTO {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  status: DriverStatus;
  cdl: {
    number: string;
    class: CDLClass;
    expirationDate: string;
    endorsements: string[];
  };
  medicalCertExpiration?: string;
  availability: {
    daysAvailable: string[];
    timeOffDates: string[];
  };
  routeAssignment?: {
    routeId: string;
    routeName: string;
    vehicleId: string;
    assignmentDate: string;
  };
  isCompliant: boolean;
  certifications: CertificationDTO[];
  hireDate: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Certification DTO
 */
export interface CertificationDTO {
  id: string;
  certificationType: string;
  certificationNumber?: string;
  issueDate: string;
  expirationDate?: string;
}

/**
 * List drivers response
 */
export interface DriversResponse {
  drivers: DriverDTO[];
  total: number;
  byStatus: {
    active: number;
    inactive: number;
    on_leave: number;
  };
}

/**
 * Create driver request
 */
export interface CreateDriverRequest {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  cdlNumber: string;
  cdlClass: CDLClass;
  cdlExpirationDate: string;
  endorsements: string[];
  medicalCertExpiration?: string;
  hireDate: string;
  availabilityDays: string[];
  notes?: string;
}

/**
 * Assign route request
 */
export interface AssignRouteRequest {
  routeId: string;
  routeName: string;
  vehicleId: string;
}

/**
 * Add certification request
 */
export interface AddCertificationRequest {
  certificationType: string;
  certificationNumber?: string;
  issueDate: string;
  expirationDate?: string;
}

/**
 * Update availability request
 */
export interface UpdateAvailabilityRequest {
  daysAvailable: string[];
  timeOffDates: string[];
}

/**
 * Update CDL request
 */
export interface UpdateCDLRequest {
  cdlNumber: string;
  cdlClass: CDLClass;
  cdlExpirationDate: string;
  endorsements: string[];
  medicalCertExpiration?: string;
}

/**
 * DriverService class
 */
export class DriverService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * List all drivers for the provider
   */
  async list(filters?: { status?: DriverStatus; availableDate?: string }): Promise<DriversResponse> {
    const params = new URLSearchParams();
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.availableDate) {
      params.append('available_date', filters.availableDate);
    }

    const queryString = params.toString();
    const endpoint = `/provider/resources/drivers${queryString ? `?${queryString}` : ''}`;

    return this.client.get<DriversResponse>(endpoint);
  }

  /**
   * Get a single driver by ID
   */
  async getById(id: string): Promise<DriverDTO> {
    return this.client.get<DriverDTO>(`/provider/resources/drivers/${id}`);
  }

  /**
   * Create a new driver
   */
  async create(data: CreateDriverRequest): Promise<DriverDTO> {
    return this.client.post<DriverDTO>('/provider/resources/drivers', data);
  }

  /**
   * Update driver details
   */
  async update(id: string, data: Partial<CreateDriverRequest>): Promise<DriverDTO> {
    return this.client.put<DriverDTO>(`/provider/resources/drivers/${id}`, data);
  }

  /**
   * Delete a driver
   */
  async delete(id: string): Promise<void> {
    return this.client.delete<void>(`/provider/resources/drivers/${id}`);
  }

  /**
   * Assign driver to route
   */
  async assignRoute(id: string, data: AssignRouteRequest): Promise<DriverDTO> {
    return this.client.post<DriverDTO>(`/provider/resources/drivers/${id}/assign-route`, data);
  }

  /**
   * Unassign driver from route
   */
  async unassignRoute(id: string): Promise<DriverDTO> {
    return this.client.post<DriverDTO>(`/provider/resources/drivers/${id}/unassign-route`, {});
  }

  /**
   * Add certification to driver
   */
  async addCertification(id: string, data: AddCertificationRequest): Promise<DriverDTO> {
    return this.client.post<DriverDTO>(`/provider/resources/drivers/${id}/certifications`, data);
  }

  /**
   * Delete certification
   */
  async deleteCertification(driverId: string, certificationId: string): Promise<DriverDTO> {
    return this.client.delete<DriverDTO>(`/provider/resources/drivers/${driverId}/certifications/${certificationId}`);
  }

  /**
   * Update availability schedule
   */
  async updateAvailability(id: string, data: UpdateAvailabilityRequest): Promise<DriverDTO> {
    return this.client.put<DriverDTO>(`/provider/resources/drivers/${id}/availability`, data);
  }

  /**
   * Update CDL information
   */
  async updateCDL(id: string, data: UpdateCDLRequest): Promise<DriverDTO> {
    return this.client.put<DriverDTO>(`/provider/resources/drivers/${id}/cdl`, data);
  }

  /**
   * Update driver status
   */
  async updateStatus(id: string, status: DriverStatus): Promise<DriverDTO> {
    return this.client.put<DriverDTO>(`/provider/resources/drivers/${id}/status`, { status });
  }
}

// Export singleton instance
export const driverService = new DriverService();
